import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { isAxiosError } from "axios";
import { useAppInitializer } from "@hooks/useAppInitializer";
import { HELP_CENTER_URLS } from "@hooks/useHelpCenterUrl";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { finishPasswordlessLogin, handleLogin } from "@lib/auth0";
import { CodeVerificationProps } from "@components/auth/CodeVerification/CodeVerification";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { Auth0Error } from "@lib/errors/auth0.error";
import { login } from "@api/auth.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { LoginFlowStart } from "./LoginFlowStart";
import { LoginFlowCodeAuth } from "./LoginFlowCodeAuth";

export const LoginFlow: React.FC = () => {
    const [finishingLogin, setFinishingLogin] = useState(false);
    const location = useLocation();
    const navigate = useStateAwareNavigate();
    const [providedPhoneOrEmail, setProvidedPhoneOrEmail] = useState<string>();
    const { error } = useToaster();
    const { initialize } = useAppInitializer();

    const handleTooManyAttemptsError = useCallback(() => {
        error(
            <>
                Having trouble logging in? Check out our{" "}
                <a href={HELP_CENTER_URLS.home.businessOwner}>Help Center</a>{" "}
                for support
            </>,
        );
    }, [error]);

    const requestCode = useCallback(
        async (phoneNumberOrEmail = providedPhoneOrEmail) => {
            if (phoneNumberOrEmail) {
                try {
                    await login(phoneNumberOrEmail);
                    return true;
                } catch (e) {
                    if (isAxiosError(e) && e.response?.status === 429) {
                        handleTooManyAttemptsError();
                    }

                    return false;
                }
            }
        },
        [handleTooManyAttemptsError, providedPhoneOrEmail],
    );

    const resendCode = useCallback(async () => {
        await requestCode();
    }, [requestCode]);

    const verifyLogin: Required<CodeVerificationProps>["verify"] = useCallback(
        async (verificationCode) => {
            if (!providedPhoneOrEmail) {
                return false;
            }

            try {
                return await finishPasswordlessLogin(
                    providedPhoneOrEmail,
                    verificationCode,
                );
            } catch (e) {
                if (
                    e instanceof Auth0Error &&
                    e.auth0Error.error === "too_many_attempts"
                ) {
                    handleTooManyAttemptsError();
                    return false;
                }
                throw e;
            }
        },
        [handleTooManyAttemptsError, providedPhoneOrEmail],
    );

    useEffect(() => {
        if (location.hash.length > 2) {
            setFinishingLogin(true);
            handleLogin()
                .then(() => initialize())
                .catch((e) => {
                    error(e.errorDescription ?? "Unknown error");
                    setFinishingLogin(false);
                    navigate("/");
                });
        }
    }, [location.hash, error, navigate, initialize]);

    if (providedPhoneOrEmail) {
        return (
            <LoginFlowCodeAuth
                onBack={() => setProvidedPhoneOrEmail(undefined)}
                providedPhoneOrEmail={providedPhoneOrEmail}
                onRequestCode={resendCode}
                onVerify={verifyLogin}
            />
        );
    } else if (finishingLogin) {
        return <PageCoverLoader>Logging in to Kick…</PageCoverLoader>;
    }

    return (
        <LoginFlowStart
            onRequestCode={requestCode}
            onSuccess={(val) => setProvidedPhoneOrEmail(val)}
        />
    );
};
